import React, { useState, useEffect } from 'react';
import {
    Table,
    Tag,
    Card,
    Statistic,
    Row,
    Col,
    Select,
    Button,
    Space,
    Modal,
    Form,
    InputNumber,
    message,
    Typography,
    Flex,
    Input,
    DatePicker,
    Collapse,
    Popconfirm
} from 'antd';
import { PlusOutlined, DollarOutlined, EditOutlined, TeamOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useStudents } from '../../hooks/useStudents';
import { usePackages } from '../../hooks/usePackages';
import { usePackagePlans } from '../../hooks/usePackagePlans';
import { useGroups } from '../../hooks/useGroups';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Payments = () => {
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const { students, loading: studentsLoading } = useStudents(selectedBranch?.id, selectedPlace?.id);
    const { packages, loading: packagesLoading, fetchPackages, addPaymentToPackage, assignPackageToStudentAction, updatePackageAction, assignBulkPackageAction, deletePackageAction } = usePackages();
    const { packagePlans } = usePackagePlans(selectedBranch?.id, selectedPlace?.id);
    const { groups } = useGroups(selectedBranch?.id, selectedPlace?.id);
    const { ageGroups } = useAgeGroups(selectedBranch?.id, selectedPlace?.id);

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [paymentModal, setPaymentModal] = useState(false);
    const [assignPackageModal, setAssignPackageModal] = useState(false);
    const [updatePackageModal, setUpdatePackageModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [paymentForm] = Form.useForm();
    const [assignForm] = Form.useForm();
    const [updateForm] = Form.useForm();
    const [bulkCardForm] = Form.useForm();

    // Hızlı tarih seçimi fonksiyonları
    const setQuickDateRange = (type) => {
        const now = dayjs();
        let startTime, endTime;

        switch (type) {
            case 'thisMonth':
                startTime = now; // Bugünden başla
                endTime = now.endOf('month'); // Ayın sonuna kadar
                break;
            case 'oneMonth':
                startTime = now;
                endTime = now.add(1, 'month');
                break;
            case 'threeMonths':
                startTime = now;
                endTime = now.add(3, 'months');
                break;
            case 'sixMonths':
                startTime = now;
                endTime = now.add(6, 'months');
                break;
            case 'oneYear':
                startTime = now;
                endTime = now.add(1, 'year');
                break;
            default:
                startTime = now;
                endTime = now.add(1, 'month');
        }

        assignForm.setFieldsValue({
            startTime: startTime,
            endTime: endTime
        });
    };

    // Toplu paket kartı için hızlı tarih seçimi
    const setBulkCardQuickDateRange = (type) => {
        const now = dayjs();
        let startTime, endTime;

        switch (type) {
            case 'thisMonth':
                startTime = now;
                endTime = now.endOf('month');
                break;
            case 'oneMonth':
                startTime = now;
                endTime = now.add(1, 'month');
                break;
            case 'threeMonths':
                startTime = now;
                endTime = now.add(3, 'months');
                break;
            case 'sixMonths':
                startTime = now;
                endTime = now.add(6, 'months');
                break;
            case 'oneYear':
                startTime = now;
                endTime = now.add(1, 'year');
                break;
            default:
                startTime = now;
                endTime = now.add(1, 'month');
        }

        bulkCardForm.setFieldsValue({
            dateRange: [startTime, endTime]
        });
    };

    // Öğrenci seçildiğinde paketlerini getir
    useEffect(() => {
        if (selectedStudent) {
            fetchPackages(selectedStudent);
        }
    }, [selectedStudent]);

    const handlePayment = (packageItem) => {
        setSelectedPackage(packageItem);
        setPaymentModal(true);
    };

    const handleUpdatePackage = (packageItem) => {
        setSelectedPackage(packageItem);
        updateForm.setFieldsValue({
            price: packageItem.price,
            paidAmount: packageItem.paidAmount,
            startTime: packageItem.startTime ? dayjs(packageItem.startTime) : null,
            endTime: packageItem.endTime ? dayjs(packageItem.endTime) : null,
            note: packageItem.note || ''
        });
        setUpdatePackageModal(true);
    };

    const handleUpdatePackageSubmit = async (values) => {
        try {
            const updateData = {
                id: selectedPackage.id,
                price: values.price,
                paidAmount: values.paidAmount,
                startTime: values.startTime?.toDate() || new Date(),
                endTime: values.endTime?.toDate() || new Date(),
                note: values.note || ''
            };

            await updatePackageAction(updateData).unwrap();
            message.success('Paket başarıyla güncellendi!');
            setUpdatePackageModal(false);
            updateForm.resetFields();
            setSelectedPackage(null);

            // Paket listesini yenile
            fetchPackages(selectedStudent);
        } catch (error) {
            console.error('Update package error:', error);
            message.error(`Paket güncellenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handlePaymentSubmit = async (values) => {
        try {
            const paymentData = {
                studentId: selectedStudent,
                paymentAmount: values.paymentAmount,
                paymentNote: values.paymentNote || ''
            };

            await addPaymentToPackage(paymentData).unwrap();
            message.success('Ödeme başarıyla alındı!');
            setPaymentModal(false);
            paymentForm.resetFields();

            // Paket listesini yenile
            fetchPackages(selectedStudent);
        } catch (error) {
            console.error('Payment error:', error);
            message.error(`Ödeme alınırken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleAssignPackage = async (values) => {
        try {
            // Eğer price alanı boş bırakıldıysa, seçilen paket planının fiyatını kullan
            const selectedPackagePlan = packagePlans.find(plan => plan.id === values.packagePlanId);
            const finalPrice = values.price || selectedPackagePlan?.price || 0;

            const assignData = {
                studentId: selectedStudent,
                packagePlanId: values.packagePlanId,
                price: finalPrice,
                paidAmount: values.paidAmount,
                startTime: values.startTime?.toDate() || new Date(),
                endTime: values.endTime?.toDate() || new Date(),
                note: values.note || ''
            };

            await assignPackageToStudentAction(assignData).unwrap();
            message.success('Paket başarıyla atandı!');
            setAssignPackageModal(false);
            assignForm.resetFields();

            // Paket listesini yenile
            fetchPackages(selectedStudent);
        } catch (error) {
            console.error('Assign package error:', error);
            message.error(`Paket atanırken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleBulkAssignPackage = async () => {
        try {
            const formValues = bulkCardForm.getFieldsValue();

            if (!formValues.packagePlanId) {
                message.warning('Lütfen paket planı seçin!');
                return;
            }

            if (!formValues.dateRange || !formValues.dateRange[0] || !formValues.dateRange[1]) {
                message.warning('Lütfen tarih aralığı seçin!');
                return;
            }

            // Eğer price alanı boş bırakıldıysa, seçilen paket planının fiyatını kullan
            const selectedPackagePlan = packagePlans.find(plan => plan.id === formValues.packagePlanId);
            const finalPrice = formValues.price || selectedPackagePlan?.price || 0;

            const bulkData = {
                branchId: selectedBranch.id,
                placeId: selectedPlace?.id,
                packagePlanId: formValues.packagePlanId,
                price: finalPrice,
                startTime: formValues.dateRange[0].toDate(),
                endTime: formValues.dateRange[1].toDate(),
                note: formValues.note || ''
            };

            // Hangi tür atama yapılacağını belirle
            if (formValues.assignType === 'all') {
                bulkData.assignToAllStudents = true;
            } else if (formValues.assignType === 'group' && formValues.groupId) {
                bulkData.groupId = formValues.groupId;
            } else if (formValues.assignType === 'ageGroup' && formValues.ageGroupId) {
                bulkData.ageGroupId = formValues.ageGroupId;
            }

            await assignBulkPackageAction(bulkData).unwrap();
            message.success('Toplu paket atama başarıyla tamamlandı!');
            bulkCardForm.resetFields();

            // Eğer bir öğrenci seçiliyse paket listesini yenile
            if (selectedStudent) {
                fetchPackages(selectedStudent);
            }
        } catch (error) {
            console.error('Bulk assign package error:', error);
            message.error(`Toplu paket atama sırasında bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleDeletePackage = async (record) => {
        await deletePackageAction(record.id).unwrap();
        message.success('Paket başarıyla silindi!');
        fetchPackages(selectedStudent);
    };

    const getStatusColor = (packageItem) => {
        if (packageItem.paidAmount >= packageItem.price) return 'success';
        if (packageItem.paidAmount > 0) return 'warning';
        return 'error';
    };

    const getStatusText = (packageItem) => {
        if (packageItem.paidAmount >= packageItem.price) return 'Ödendi';
        if (packageItem.paidAmount > 0) return 'Kısmi Ödeme';
        return 'Bekliyor';
    };

    const packageColumns = [
        {
            title: 'Paket Adı',
            dataIndex: 'packagePlanName',
            key: 'packagePlanName',
        },
        {
            title: 'Toplam Fiyat',
            dataIndex: 'price',
            key: 'price',
            render: value => `₺${value?.toLocaleString() || 0}`
        },
        {
            title: 'Ödenen',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            render: value => `₺${value?.toLocaleString() || 0}`
        },
        {
            title: 'Kalan',
            key: 'remaining',
            render: (_, record) => {
                const remaining = (record.price || 0) - (record.paidAmount || 0);
                return `₺${remaining.toLocaleString()}`;
            }
        },
        {
            title: 'Durum',
            key: 'status',
            render: (_, record) => (
                <Tag color={getStatusColor(record)}>
                    {getStatusText(record)}
                </Tag>
            )
        },
        {
            title: 'Kayıt Tarihi',
            key: 'registrationDate',
            render: (_, record) => {
                const registrationDate = record.students && record.students[0] && record.students[0].registrationDate;
                return registrationDate ? dayjs(registrationDate).format('DD/MM/YYYY') : '-';
            },
        },
        {
            title: 'Başlangıç Tarihi',
            dataIndex: 'startTime',
            key: 'startTime',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Bitiş Tarihi',
            dataIndex: 'endTime',
            key: 'endTime',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Ödeme Tarihi',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Not',
            dataIndex: 'note',
            key: 'note',
            render: value => value || '-'
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleUpdatePackage(record)}
                    />
                    <Popconfirm
                        title="Bu ödemeyi silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDeletePackage(record)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            type="primary"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}

                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Seçili öğrencinin paketlerini filtrele ve sırala
    const filteredPackages = selectedStudent
        ? packages
            .filter(pkg => {
                // Paketin students array'inde seçili öğrenci var mı kontrol et
                const hasStudent = pkg.students && pkg.students.some(student => student.id === selectedStudent);
                return hasStudent;
            })
            .sort((a, b) => {
                const dateA = a.endTime ? dayjs(a.endTime).valueOf() : 0;
                const dateB = b.endTime ? dayjs(b.endTime).valueOf() : 0;
                return dateB - dateA; // Yeni tarihler önce (uzak bitiş tarihi önce)
            })
        : [];


    const totalPaid = filteredPackages.reduce((sum, pkg) =>
        sum + (pkg.paidAmount || 0), 0
    );

    const totalRemaining = filteredPackages.reduce((sum, pkg) =>
        sum + ((pkg.price || 0) - (pkg.paidAmount || 0)), 0
    );

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Ödemeleri yönetmek için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <Flex vertical gap="middle" style={{ padding: '16px' }}>

            {/* Toplu Paket Tanımlama Collapse */}
            <Collapse
                items={[
                    {
                        key: '1',
                        label: 'Toplu Paket Tanımla',
                        children: (
                            <Form form={bulkCardForm} layout="vertical">
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="packagePlanId" label="Paket Planı">
                                            <Select placeholder="Paket planı seçin" style={{ width: '100%' }}>
                                                {packagePlans.map(plan => (
                                                    <Option key={plan.id} value={plan.id}>
                                                        {plan.name} - {plan.price} TL
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="price" label="Fiyat">
                                            <InputNumber
                                                placeholder="Fiyat (opsiyonel)"
                                                style={{ width: '100%' }}
                                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="dateRange" label="Tarih Aralığı">
                                            <DatePicker.RangePicker
                                                style={{ width: '100%' }}
                                                format="DD/MM/YYYY"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Hızlı Tarih Seçimi">
                                            <Space wrap>
                                                <Button size="small" onClick={() => setBulkCardQuickDateRange('thisMonth')}>
                                                    Bu Ay
                                                </Button>
                                                <Button size="small" onClick={() => setBulkCardQuickDateRange('oneMonth')}>
                                                    1 Ay
                                                </Button>
                                                <Button size="small" onClick={() => setBulkCardQuickDateRange('threeMonths')}>
                                                    3 Ay
                                                </Button>
                                                <Button size="small" onClick={() => setBulkCardQuickDateRange('sixMonths')}>
                                                    6 Ay
                                                </Button>
                                                <Button size="small" onClick={() => setBulkCardQuickDateRange('oneYear')}>
                                                    1 Yıl
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="assignType" label="Atama Türü" initialValue="all">
                                            <Select placeholder="Atama türü seçin">
                                                <Option value="all">Bütün Aktif Öğrencilere</Option>
                                                <Option value="group">Gruba Göre</Option>
                                                <Option value="ageGroup">Yaş Grubuna Göre</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) => prevValues.assignType !== currentValues.assignType}
                                        >
                                            {({ getFieldValue }) => {
                                                const assignType = getFieldValue('assignType');

                                                if (assignType === 'group') {
                                                    return (
                                                        <Form.Item
                                                            name="groupId"
                                                            label="Grup Seçin"
                                                            rules={[{ required: true, message: 'Lütfen grup seçin!' }]}
                                                        >
                                                            <Select placeholder="Grup seçin">
                                                                {groups.map(group => (
                                                                    <Option key={group.id} value={group.id}>
                                                                        {group.name}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }

                                                if (assignType === 'ageGroup') {
                                                    return (
                                                        <Form.Item
                                                            name="ageGroupId"
                                                            label="Yaş Grubu Seçin"
                                                            rules={[{ required: true, message: 'Lütfen yaş grubu seçin!' }]}
                                                        >
                                                            <Select placeholder="Yaş grubu seçin">
                                                                {ageGroups.map(ageGroup => (
                                                                    <Option key={ageGroup.id} value={ageGroup.id}>
                                                                        {ageGroup.name}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }

                                                return <Form.Item label="Hedef Kitle">
                                                    <Text type="secondary">Bütün aktif öğrencilere paket atanacak</Text>
                                                </Form.Item>;
                                            }}
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24}>
                                        <Form.Item name="note" label="Not">
                                            <Input placeholder="Paket notu (opsiyonel)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={24}>
                                        <Popconfirm
                                            title="Toplu Paket Atama"
                                            description="Bu işlem geri alınamaz. Emin misiniz?"
                                            onConfirm={handleBulkAssignPackage}
                                            okText="Evet"
                                            cancelText="Hayır"
                                            placement="top"
                                        >
                                            <Button
                                                type="primary"
                                                icon={<TeamOutlined />}
                                                size="large"
                                                style={{ width: '100%' }}
                                            >
                                                Toplu Paket Ata
                                            </Button>
                                        </Popconfirm>
                                    </Col>
                                </Row>
                            </Form>
                        )
                    }
                ]}
            />

            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Öğrenci Seçiniz"
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={setSelectedStudent}
                            loading={studentsLoading}
                        >
                            {students.map(student => (
                                <Option key={student.id} value={student.id}>
                                    {student.fullName}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setAssignPackageModal(true)}
                                disabled={!selectedStudent}
                            >
                                Yeni Paket Ata
                            </Button>
                            <Button
                                type="primary"
                                icon={<DollarOutlined />}
                                onClick={() => {
                                    if (filteredPackages.length > 0) {
                                        const unpaidPackage = filteredPackages.find(pkg => pkg.paidAmount < pkg.price);
                                        if (unpaidPackage) {
                                            handlePayment(unpaidPackage);
                                        } else {
                                            message.info('Ödenmemiş paket bulunmamaktadır.');
                                        }
                                    } else {
                                        message.info('Önce paket atayınız.');
                                    }
                                }}
                                disabled={!selectedStudent || filteredPackages.length === 0}
                            >
                                Ödeme Al
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {selectedStudent && (
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Card>
                                <Statistic
                                    title="Toplam Ödenen"
                                    value={totalPaid}
                                    precision={2}
                                    valueStyle={{ color: '#3f8600' }}
                                    prefix={<DollarOutlined />}
                                    suffix="₺"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card>
                                <Statistic
                                    title="Kalan Ödeme"
                                    value={totalRemaining}
                                    precision={2}
                                    valueStyle={{ color: totalRemaining > 0 ? '#cf1322' : '#3f8600' }}
                                    prefix={<DollarOutlined />}
                                    suffix="₺"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card>
                                <Statistic
                                    title="Toplam Paket"
                                    value={filteredPackages.length}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                    </Row>
                )}
            </Card>

            {selectedStudent && (
                <Table
                    columns={packageColumns}
                    dataSource={filteredPackages}
                    rowKey="id"
                    loading={packagesLoading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} paket`
                    }}
                    locale={{
                        emptyText: packagesLoading ? 'Yükleniyor...' : 'Henüz paket bulunmamaktadır'
                    }}
                />
            )}

            {/* Ödeme Alma Modalı */}
            <Modal
                title="Ödeme Al"
                open={paymentModal}
                onCancel={() => {
                    setPaymentModal(false);
                    setSelectedPackage(null);
                    paymentForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={paymentForm}
                    layout="vertical"
                    onFinish={handlePaymentSubmit}
                >
                    <Form.Item
                        name="paymentAmount"
                        label="Ödeme Miktarı"
                        rules={[{ required: true, message: 'Lütfen ödeme miktarı girin!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Ödeme miktarı"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="paymentNote"
                        label="Ödeme Notu"
                    >
                        <TextArea rows={3} placeholder="Ödeme ile ilgili not" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Ödeme Al
                            </Button>
                            <Button onClick={() => {
                                setPaymentModal(false);
                                setSelectedPackage(null);
                                paymentForm.resetFields();
                            }}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Paket Atama Modalı */}
            <Modal
                title="Yeni Paket Ata"
                open={assignPackageModal}
                onCancel={() => {
                    setAssignPackageModal(false);
                    assignForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={assignForm}
                    layout="vertical"
                    onFinish={handleAssignPackage}
                >
                    <Form.Item
                        name="packagePlanId"
                        label="Paket Planı"
                        rules={[{ required: true, message: 'Lütfen paket planı seçin!' }]}
                    >
                        <Select placeholder="Paket planı seçin" allowClear>
                            {packagePlans.map(packagePlan => (
                                <Option key={packagePlan.id} value={packagePlan.id}>
                                    {packagePlan.name} - {packagePlan.price} TL
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="price"
                        label="Paket Fiyatı"
                        extra="Boş bırakılırsa plan fiyatı uygulanır"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Paket fiyatı"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="paidAmount"
                        label="Ödenen Miktar"
                        initialValue={0}
                        rules={[{ required: true, message: 'Lütfen ödenen miktarı girin!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Ödenen miktar"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="startTime"
                        label="Başlangıç Tarihi"
                        rules={[{ required: true, message: 'Lütfen başlangıç tarihi seçin!' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Başlangıç tarihi"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="endTime"
                        label="Bitiş Tarihi"
                        rules={[{ required: true, message: 'Lütfen bitiş tarihi seçin!' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Bitiş tarihi"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    {/* Hızlı Tarih Seçimi Butonları */}
                    <Form.Item label="Hızlı Tarih Seçimi">
                        <Space wrap>
                            <Button size="small" onClick={() => setQuickDateRange('thisMonth')}>
                                Bu Ay
                            </Button>
                            <Button size="small" onClick={() => setQuickDateRange('oneMonth')}>
                                1 Ay
                            </Button>
                            <Button size="small" onClick={() => setQuickDateRange('threeMonths')}>
                                3 Ay
                            </Button>
                            <Button size="small" onClick={() => setQuickDateRange('sixMonths')}>
                                6 Ay
                            </Button>
                            <Button size="small" onClick={() => setQuickDateRange('oneYear')}>
                                1 Yıl
                            </Button>
                        </Space>
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label="Paket Notu"
                    >
                        <TextArea rows={3} placeholder="Paket ile ilgili not" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Paket Ata
                            </Button>
                            <Button onClick={() => {
                                setAssignPackageModal(false);
                                assignForm.resetFields();
                            }}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Paket Güncelleme Modalı */}
            <Modal
                title="Paket Güncelle"
                open={updatePackageModal}
                onCancel={() => {
                    setUpdatePackageModal(false);
                    setSelectedPackage(null);
                    updateForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={updateForm}
                    layout="vertical"
                    onFinish={handleUpdatePackageSubmit}
                >
                    <Form.Item
                        name="price"
                        label="Paket Fiyatı"
                        rules={[{ required: true, message: 'Lütfen paket fiyatı girin!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Paket fiyatı"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="paidAmount"
                        label="Ödenen Miktar"
                        rules={[{ required: true, message: 'Lütfen ödenen miktarı girin!' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Ödenen miktar"
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="startTime"
                        label="Başlangıç Tarihi"
                        rules={[{ required: true, message: 'Lütfen başlangıç tarihi seçin!' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Başlangıç tarihi"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="endTime"
                        label="Bitiş Tarihi"
                        rules={[{ required: true, message: 'Lütfen bitiş tarihi seçin!' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="Bitiş tarihi"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label="Paket Notu"
                    >
                        <TextArea rows={3} placeholder="Paket ile ilgili not" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Güncelle
                            </Button>
                            <Button onClick={() => {
                                setUpdatePackageModal(false);
                                setSelectedPackage(null);
                                updateForm.resetFields();
                            }}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>


        </Flex>
    );
};

export default Payments;